import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { useUpdateMediaAiTrainingDataApi } from '@/common/api/modules/mediaAiTrainingData'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { useShowSavedToast } from '@/common/app/toast'
import {
  ARTICLE_HTML_MAX_LENGTH,
  MediaAiTrainingData
} from '@/common/model/media/mediaAiTrainingData'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  ArticleHtmlSchemaType,
  articleHtmlNameLabelMapping,
  articleHtmlSchema,
  convertArticleHtmlSchemaToVars,
  convertArticleHtmlToSchema
} from '@/components/container/form/modules/media/aiTrainingData/articleHtmlSchema'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormTextArea from '@/components/container/form/widgets/FormTextArea'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'
import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'

interface Props {
  data: MediaAiTrainingData
  onRefetch?(): void
}

// 記事HTML（コンテンツHTMLの型）の登録。サイトごとに1件で、保存のたびに上書きする。
// 空にして保存すると未登録の状態へ戻り、AI記事作成は型なしの従来生成に戻る。
const ArticleHtmlForm = ({ data, onRefetch }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<ArticleHtmlSchemaType>({
    resolver: zodResolver(articleHtmlSchema),
    values: convertArticleHtmlToSchema(data),
    mode: 'onChange'
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, articleHtmlNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: true })

  const { trigger } = useUpdateMediaAiTrainingDataApi({
    onSuccess: () => {
      showSaved('記事HTML')
      onRefetch?.()
    }
  })

  const onSubmit: SubmitHandler<ArticleHtmlSchemaType> = (formData) =>
    trigger(convertArticleHtmlSchemaToVars(formData))

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageTitle
        title={'生成フォーマット'}
        badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
      />
      {isDirty && (
        <TopBar
          status={TopBarStatus.CHANGE}
          captionText='未反映の変更があります'
          onCancel={() => reset(convertArticleHtmlToSchema(data))}
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading subText='AIに再現させたい構成や記述形式のサンプルを入力してください。1パターンのみ登録可能です。'>
            記事HTML
          </Heading>
          <AreaContent>
            <FormRow direction='horizon' gap={4} size={TextBoxSize.FULL}>
              <FormLabel id='articleHtml' required>
                記事HTML
              </FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormTextArea
                  name='articleHtml'
                  register={register('articleHtml')}
                  control={control}
                  error={!!errors.articleHtml?.message}
                  maxLength={ARTICLE_HTML_MAX_LENGTH}
                  placeholder={
                    '<div class="article-body">\n  <h2>見出し</h2>\n  <p>本文</p>\n</div>'
                  }
                />
                <FormCaption size={12}>
                  登録した内容は、AIがコンテンツを生成する際に構成や記述形式の参考として使用します。出力結果への完全な反映を保証するものではありません。
                </FormCaption>
                <FormError message={errors.articleHtml?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
      </Content>
      <PageBottom>
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          保存する
        </Button>
      </PageBottom>
    </form>
  )
}

export default ArticleHtmlForm
